import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  MainMenu,
  MainMenuItem,
  MainMenuSection
} from "@redelair-sa/intiza-ui";
import { TranslationService } from "../../../services/TranslationService";
import CompanyService from "../../../services/CompanyService";
import TabPositionContext from "../TabPositionContext";
import CounterMsgContext from "../CounterMsgContext";
import { getLinkTo } from "../utils/LinkUtils";

// MainMenu marca como activo todo item cuyo id sea prefijo del id activo, asi
// que el dashboard de consolidado no puede usar "consolidated" como id: se
// prendia junto a "consolidated/clients" y "consolidated/report". Usa un id
// propio que se traduce a su ruta al navegar.
const CONSOLIDATED_DASHBOARD_ID = "consolidated/dashboard";

const routeByMenuId: Record<string, string> = {
  [CONSOLIDATED_DASHBOARD_ID]: "consolidated"
};

const resolveMenuRouteId = (id: string) => routeByMenuId[id] ?? id;
const getMenuItemHref = (id: string) => getLinkTo(resolveMenuRouteId(id));

const withMenuHrefs = (items: MainMenuItem[]): MainMenuItem[] =>
  items.map((item) =>
    item.children?.length
      ? { ...item, children: withMenuHrefs(item.children) }
      : { ...item, href: getMenuItemHref(item.id) }
  );

const AppMainMenu: React.FC = () => {
  const { currentTabPosition } = useContext(TabPositionContext);
  const { inboxCount } = useContext(CounterMsgContext);
  const [showWhatsappCounter, setShowWhatsappCounter] = useState(true);
  const history = useHistory();
  const location = useLocation();

  const isConsolidatedRoute = location.pathname.includes("/consolidated");
  const isConsolidatedDetail =
    new URLSearchParams(location.search).get("consolidated") === "1";
  const isConsolidatedView =
    CompanyService.canViewConsolidated() &&
    (isConsolidatedRoute || isConsolidatedDetail);

  useEffect(() => {
    setShowWhatsappCounter(CompanyService.canUseWhatsapp());
  }, []);

  const reportItems: MainMenuItem[] = [
    {
      id: "reports-debt",
      label: TranslationService.translate.DebtAnalysis,
      children: [
        {
          id: "report/ReportReceivablesSummary",
          label: TranslationService.translate.CollectionAnalisis
        },
        {
          id: "report/collection",
          label: TranslationService.translate.RecoveryAnalysis
        },
        {
          id: "report/reportaginggroup",
          label: TranslationService.translate.AgingAnalysis
        }
      ]
    },
    {
      id: "reports-clients",
      label: TranslationService.translate.Clients,
      children: [
        {
          id: "report/client",
          label: TranslationService.translate.CustomerAnalysis
        },
        {
          id: "report/persongroup",
          label: TranslationService.translate.ClientSummary
        },
        {
          id: "report/reportclientmap",
          label: TranslationService.translate.ClientsMap
        },
        {
          id: "report/credit",
          label: TranslationService.translate.CreditAnalysis
        }
      ]
    },
    {
      id: "reports-invoices-payments",
      label: `${TranslationService.translate.Invoices} ${TranslationService.translate.And.toLocaleLowerCase()} ${TranslationService.translate.Payments}`,
      children: [
        {
          id: "report/iolist",
          label: TranslationService.translate.HeaderInvoices
        },
        {
          id: "report/invoicegroup",
          label: TranslationService.translate.InvoiceSummary
        },
        {
          id: "report/iostatuslist",
          label: TranslationService.translate.InvoiceStatusList
        },
        {
          id: "report/payment",
          label: TranslationService.translate.Transaction
        },
        {
          id: "report/paymentgroup",
          label: TranslationService.translate.PaymentSummary
        },
        {
          id: "report/reportpaymentanalysis",
          label: TranslationService.translate.PaymentAnalysis
        }
      ]
    },
    {
      id: "reports-activities",
      label: TranslationService.translate.Activities,
      children: [
        {
          id: "report/reportactivity",
          label: TranslationService.translate.Activities
        },
        {
          id: "report/reportactivitysummary",
          label: TranslationService.translate.ActivitySummary
        },
        ...(CompanyService.getSetting("enablemessageiolink")
          ? [
              {
                id: "report/ioactivity",
                label: TranslationService.translate.ReportInvociesActivities
              }
            ]
          : []),
        {
          id: "report/activitytypesummary",
          label: TranslationService.translate.ActivitiesTypes
        },
        ...(CompanyService.getTags()?.length
          ? [{ id: "report/taglist", label: TranslationService.translate.Tags }]
          : [])
      ]
    },
    {
      id: "reports-other",
      label: TranslationService.translate.Other,
      children: [
        {
          id: "report/reportexport",
          label: TranslationService.translate.Templates
        },
        {
          id: "report/scheduled",
          label: TranslationService.translate.ScheduledReports
        },
        ...(TranslationService.getCurrentLanguage() === "es"
          ? [{ id: "report/log", label: "Registro" }]
          : [])
      ]
    }
  ];

  const items: MainMenuItem[] = [
    {
      id: "company",
      label: TranslationService.translate.Dashboard,
      icon: <i className="fa-solid fa-fw fa-gauge-high" />
    },
    {
      id: "client",
      label: TranslationService.translate.Clients,
      icon: <i className="fa-solid fa-fw fa-users" />
    },
    {
      id: "inbox",
      label: TranslationService.translate.Inbox,
      icon: <i className="fa-solid fa-fw fa-inbox" />,
      badge: showWhatsappCounter ? inboxCount?.count : inboxCount?.countMail
    },
    {
      id: "task",
      label: TranslationService.translate.Tasks,
      icon: <i className="fa-solid fa-fw fa-list-check" />
    },
    {
      id: "segment",
      label: TranslationService.translate.Segments,
      icon: <i className="fa-solid fa-fw fa-layer-group" />
    },
    ...(CompanyService.getIsOwner() ||
    CompanyService.canDoAny(["viewaction", "editaction"])
      ? [
          {
            id: "action",
            label: TranslationService.translate.Automatic,
            icon: <i className="fa-solid fa-fw fa-bolt" />
          }
        ]
      : []),
    ...(CompanyService.canDo("viewreport")
      ? [
          {
            id: "Reports",
            label: TranslationService.translate.Reports,
            icon: <i className="fa-solid fa-fw fa-chart-line" />,
            children: reportItems
          }
        ]
      : [])
  ];

  const consolidatedItems: MainMenuItem[] = [
    {
      id: CONSOLIDATED_DASHBOARD_ID,
      label: TranslationService.translate.Dashboard || "Principal",
      icon: <i className="fa-solid fa-fw fa-gauge-high" />
    },
    {
      id: "consolidated/clients",
      label: TranslationService.translate.Clients,
      icon: <i className="fa-solid fa-fw fa-users" />
    },
    {
      id: "consolidated/report",
      label: TranslationService.translate.Reports,
      icon: <i className="fa-solid fa-fw fa-chart-line" />
    }
  ];

  const menuItems = isConsolidatedView ? consolidatedItems : items;

  const sections: MainMenuSection[] = [
    {
      title: TranslationService.translate.MainMenuSectionTitle,
      items: withMenuHrefs(menuItems)
    }
  ];

  const handleItemClick = (item: MainMenuItem) => {
    history.push(getMenuItemHref(item.id));
  };

  return (
    <MainMenu
      sections={sections}
      activeItemId={currentTabPosition ?? undefined}
      workspace="cobranzas"
      onItemClick={handleItemClick}
    />
  );
};

export default AppMainMenu;
